import { Err, isErr } from '@pacote/result';
import { isAxiosError } from 'services/api/api.helpers';
import { baseResponseSchema } from 'services/api/api.types';
import { z } from 'zod/v4';
/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

export const saveSelfLimitationsSchemaPayload = z.object({
  requestObject: z.object({
    selfLimitations: z.array(
      z.object({
        accountCode: z.string(),
        typeCode: z.string(),
        newLimitAmount: z.number(),
      }),
    ),
  }),
});

type AccountLimitationRequest = z.infer<
  typeof saveSelfLimitationsSchemaPayload
>;
const accountLimitationResponseSchema = baseResponseSchema.extend({
  selfLimitations: z.array(
    z.object({
      accountCode: z.string(),
      typeCode: z.string(),
      newLimitAmount: z.number(),
    }),
  ),
});

type AccountLimitationIResponse = z.infer<
  typeof accountLimitationResponseSchema
>;

type AccountLimitationErroResponse = {
  type: 'UNKNOWN' | 'ERROR_MESSAGE' | 'ALERT_BAR' | 'SURVEY';
  errorMessage?: string;
  alertBarData?: {
    id: string;
    data?: string | Record<string, unknown>;
    type: 'ERROR' | 'INFO';
    errorPermanent?: boolean;
  };
};

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

const calculate7DaysFromNowOn = () => {
  const today = new Date();
  today.setDate(today.getDate() + 7);
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();

  const formattedDate = `${day}/${month}/${year}`;
  return formattedDate;
};

function transformAccountLimitationErrorResponse(errorResponse: unknown) {
  if (!isAxiosError<any>(errorResponse)) {
    console.error('Non-axios error:', errorResponse);
    return { type: 'UNKNOWN' };
  }

  if (!errorResponse.response?.data) {
    console.error('Axios error without response data:', errorResponse);
    return { type: 'UNKNOWN' };
  }

  const rawErrorMessage = errorResponse?.response?.data?.error?.message;

  try {
    //Questa roba è un pò folle ma ormai si balla
    const parsedMessage = JSON.parse(rawErrorMessage);

    if (!parsedMessage?.code) return Err({ type: 'UNKNOWN' });

    const secondPartOfCode = parsedMessage.code.split('-')[1];

    if (!secondPartOfCode) return Err({ type: 'UNKNOWN' });

    if (secondPartOfCode === '1003' || secondPartOfCode === '1103')
      return Err({
        type: 'SURVEY',
      });

    if (secondPartOfCode === '1001')
      return Err({
        type: 'ERROR_MESSAGE',
        errorMessage: 'text.preferences.error.limit.survey.failed',
        alertBarData: {
          id: 'text.preferences.error.limit.survey.firstTime.failed',
          type: 'ERROR',
          data: { date: calculate7DaysFromNowOn() },
          errorPermanent: true,
        },
      });

    if (secondPartOfCode === '1002')
      return Err({
        type: 'ERROR_MESSAGE',
        errorMessage: 'text.preferences.error.limit.survey.failed',
        alertBarData: {
          id: 'text.preferences.error.limit.surveyFailed.7days.increase',
          type: 'INFO',
        },
      });

    if (secondPartOfCode === '1101' || secondPartOfCode === '1102')
      return Err({
        type: 'ERROR_MESSAGE',
        errorMessage: 'text.preferences.error.limit.3months.increase.default',
        alertBarData: {
          id: 'text.preferences.error.limit.3months.increase.default',
          type: 'INFO',
        },
      });
    return Err({ type: 'UNKNOWN' });
  } catch (error) {
    console.error('Error parsing error message:', error);
    return Err({ type: 'UNKNOWN' });
  }
}

function isAccountLimitationErrorResponse(
  response: any,
): response is Err<AccountLimitationErroResponse> {
  try {
    return isErr(response);
  } catch (error) {
    return false;
  }
}

export {
  accountLimitationResponseSchema,
  isAccountLimitationErrorResponse,
  transformAccountLimitationErrorResponse,
};
export type { AccountLimitationIResponse, AccountLimitationRequest };
